/**
 * aiParse.js — AI 识别结果归一化层
 *
 * 为什么需要这一层：
 * 后端 OCR / 语音解析返回的 parsed 结构与前端表单的期望之间存在三处错位，
 * 错位会让「识别成功」的流程在最后一步静默失败，用户感知就是「这个功能没用」。
 *
 * 三处错位及后果：
 *  1) dosage 里混着单位。后端返回 "100mg" / "1片"，前端表单是 <input type="number">，
 *     而且提交前有 Number(dosage) 的正数校验 —— 结果必然是 NaN，被拦在「剂量必须是大于0的正数」。
 *     这就是拍照/语音录入「识别到了却存不进去」的直接原因。
 *  2) 后端返回 rawText（驼峰），前端读 raw_text（下划线）—— 识别出的原文永远不显示。
 *  3) warnings 是 { field, message } 对象数组，前端直接当字符串渲染 —— React 直接白屏。
 *
 * 本层只做「形状归一」，不改变任何业务语义、不做医学判断。
 * 采用防御式写法：新老两种字段名、字符串与对象两种消息形态都能吃下，
 * 这样后端将来再改字段名，界面也不会崩。
 */

/** 支持的剂量单位。按「长优先」排列，避免「毫克」被「克」抢先匹配 */
const UNIT_TOKENS = ['毫克', '毫升', 'mg', 'ml', 'g', '克', '片', '粒', '袋', '滴', '丸', '支', '喷', '单位'];

/** 英文字母单位统一成中文，与表单下拉框的选项对齐 */
const UNIT_ALIAS = {
  mg: '毫克',
  ml: '毫升',
  g: '克',
  iu: '单位',
};

/**
 * 把可能带单位的剂量拆成「纯数字 + 单位」。
 * splitDosage('100mg')  -> { dosage: '100', unit: '毫克' }
 * splitDosage('1片')    -> { dosage: '1',   unit: '片'   }
 * splitDosage(0.5)      -> { dosage: '0.5', unit: ''     }
 * 无法解析出合法数字时返回空字符串，交给界面提示用户手填。
 */
export function splitDosage(raw) {
  if (raw === undefined || raw === null) return { dosage: '', unit: '' };
  if (typeof raw === 'number') {
    return isFinite(raw) && raw > 0 ? { dosage: String(raw), unit: '' } : { dosage: '', unit: '' };
  }

  const text = String(raw).trim();
  if (!text) return { dosage: '', unit: '' };

  // 取开头的数字部分，剩下的当作单位
  const matched = text.match(/^([\d.]+)\s*(.*)$/);
  if (!matched) return { dosage: '', unit: '' };

  const num = matched[1].replace(/\.$/, '');
  const parsed = Number(num);
  if (!num || isNaN(parsed) || parsed <= 0) return { dosage: '', unit: '' };

  const rest = (matched[2] || '').trim();
  let unit = '';
  if (rest) {
    const token = UNIT_TOKENS.find(t => rest.toLowerCase().startsWith(t.toLowerCase()));
    if (token) unit = UNIT_ALIAS[token.toLowerCase()] || token;
  }

  return { dosage: num, unit };
}

/**
 * 把 messages 归一成字符串数组。
 * 能吃下：['文本'] / [{ message: '文本' }] / [{ field, message }] / undefined
 */
export function normalizeMessages(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.message || item.msg || item.text || '';
      return '';
    })
    .filter(Boolean);
}

/** 时间点校验，只接受 HH:mm */
function sanitizeTimes(times) {
  if (!Array.isArray(times)) return [];
  return times
    .map(t => String(t).trim())
    .filter(t => /^([01]\d|2[0-3]):[0-5]\d$/.test(t));
}

/**
 * 把后端 parsed 归一成表单可直接消费的结构。
 * 输出的 dosage 一定是纯数字字符串，unit 一定与下拉选项同构。
 */
export function normalizeParsed(parsed, fallbackText = '') {
  const src = parsed || {};
  const { dosage, unit } = splitDosage(src.dosage);

  const rawText = String(fallbackText || src.rawText || src.raw_text || '');
  const method = typeof src.method === 'string' ? src.method.trim() : '';
  const notes = typeof src.notes === 'string' ? src.notes.trim() : '';

  return {
    name: typeof src.name === 'string' ? src.name.trim() : '',
    dosage,
    unit: unit || (typeof src.unit === 'string' ? src.unit.trim() : ''),
    frequency: typeof src.frequency === 'string' ? src.frequency.trim() : '',
    times: sanitizeTimes(src.times),
    method,
    // 后端常常只回 method 不回 notes，这里补一个可读的默认值，避免用户再手填一遍
    notes: notes || (method ? `${method}服用` : ''),
    rawText,
  };
}

/**
 * 生成「只覆盖识别到的字段」的表单补丁。
 *
 * 为什么不直接整体替换：用户可能已经手动填了一半，识别结果里某个字段为空时
 * 整体替换会把已填内容清空 —— 这是原实现的隐藏坑（识别失败反而丢失用户输入）。
 */
export function buildFormPatch(parsed) {
  const patch = {};
  if (parsed.name) patch.name = parsed.name;
  if (parsed.dosage) patch.dosage = parsed.dosage;
  if (parsed.unit) patch.unit = parsed.unit;
  if (parsed.frequency) patch.frequency = parsed.frequency;
  if (parsed.times && parsed.times.length > 0) patch.times = parsed.times;
  if (parsed.method) patch.method = parsed.method;
  if (parsed.notes) patch.notes = parsed.notes;
  return patch;
}

/**
 * 算出「识别到什么 / 没识别到什么」，用于向用户如实交代。
 * 对老年用户来说，「没识别到剂量」比「假装成功了」重要得多。
 */
export function describeRecognition(parsed) {
  const got = [];
  const missing = [];
  const check = (label, ok) => (ok ? got : missing).push(label);

  check('药品名称', !!parsed.name);
  check('剂量', !!parsed.dosage);
  check('服用频次', !!parsed.frequency);
  check('提醒时间', !!(parsed.times && parsed.times.length));
  check('服用方式', !!parsed.method);

  return { got, missing };
}

/**
 * 归一「核对」结果的渲染结构。
 * 注意口径：后端返回的这份结果来自**规则引擎**（正则 + 人工整理的参考库），
 * 不是大模型给出的判断。界面上不要说成 AI，避免把猜测包装成结论。
 * 兼容 warnings 为字符串数组（原始契约）与对象数组（后来实现）两种形态。
 */
export function normalizeValidation(result) {
  const src = result || {};
  return {
    level: src.level || 'ok',
    warnings: normalizeMessages(src.warnings),
    errors: normalizeMessages(src.errors),
    suggestions: normalizeMessages(src.suggestions),
    drugInfo: src.drugInfo || null,
  };
}
